import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, RefreshCw, X as XIcon } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

export const RefundQueue = ({ embedded = false }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [denyModal, setDenyModal] = useState(null);
  const [denyNote, setDenyNote] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/refund-queue`).then((r) => setRows(r.data.requests || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    const amt = Number(r.pending_refund_request?.refund_amount ?? r.refund_amount ?? 0);
    if (!window.confirm(`Approve refund of $${amt.toFixed(2)} to ${r.customer_email}?`)) return;
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/refund-queue/${r.id}/approve`);
      const st = data?.refund?.status;
      const amt = Number(data.refund_amount ?? 0).toFixed(2);
      if (st === "succeeded") {
        toast.success(`Refund of $${amt} issued and settled by Stripe ✨`);
      } else if (st === "pending") {
        toast.success(`Refund of $${amt} accepted by Stripe · will settle on the card in 5–10 business days`);
      } else {
        toast.success(`Refund submitted (status: ${st || "unknown"}) — check the Stripe dashboard`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setBusy((b) => ({ ...b, [r.id]: false }));
    }
  };
  const submitDeny = async () => {
    if (!denyModal) return;
    setBusy((b) => ({ ...b, [denyModal.id]: true }));
    try {
      await axios.post(`${API}/admin/refund-queue/${denyModal.id}/deny`, { note: denyNote });
      toast.success("Request denied.");
      setDenyModal(null); setDenyNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deny failed");
    } finally {
      setBusy((b) => ({ ...b, [denyModal.id]: false }));
    }
  };

  return (
    <div>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl">Refund Queue</h1>
          <Btn variant="ghost" onClick={load} data-testid="refunds-reload"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end mb-4">
          <Btn variant="ghost" onClick={load} data-testid="refunds-reload"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
        </div>
      )}
      {!rows ? <div className="text-[#B9C8D5]">Loading…</div> : rows.length === 0 ? (
        <Card className="p-10 text-center text-[#B9C8D5]" data-testid="refunds-empty">
          🎉 No pending refunds. All caught up.
        </Card>
      ) : (
        <div className="space-y-4" data-testid="refunds-list">
          {rows.map((r) => {
            const req = r.pending_refund_request;
            const isItemRemoval = req?.type === "item_removal";
            const amount = req?.refund_amount ?? r.refund_amount ?? 0;
            const percent = req?.refund_percent ?? r.refund_percent ?? 0;
            const label = req?.refund_policy_label ?? r.refund_policy_label ?? "";
            return (
              <Card key={r.id} className="p-5" data-testid={`refund-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">
                      {isItemRemoval ? "Item removal request" : "Cancellation refund"} · #{r.id.slice(0, 8).toUpperCase()}
                    </div>
                    <div className="font-serif text-xl mt-1">{r.customer_name}</div>
                    <div className="text-sm text-[#B9C8D5]">
                      <a href={`mailto:${r.customer_email}`} className="hover:text-[#FFD166]">{r.customer_email}</a>
                      {r.customer_phone && <> · <a href={`tel:${r.customer_phone}`} className="hover:text-[#FFD166]">{r.customer_phone}</a></>}
                    </div>
                    <div className="text-xs text-[#B9C8D5] mt-1">Delivery {r.delivery_date} → {r.pickup_date} · {r.delivery_resort}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-3xl text-[#FFD166]">${amount.toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">{percent}% · {label}</div>
                  </div>
                </div>

                {isItemRemoval && (
                  <div className="mt-4 p-3 rounded-lg border border-[#FFD166]/20 bg-black/25">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166]">Items to remove</div>
                    <ul className="text-sm mt-1 space-y-0.5">
                      {(req.line_items_to_remove || []).map((li, i) => (
                        <li key={li.product_id}>× {li.quantity} {li.name} <span className="text-[#B9C8D5]">(subtotal ${li.subtotal.toFixed(2)})</span></li>
                      ))}
                    </ul>
                    {req.reason && <p className="text-xs text-[#B9C8D5] mt-2 italic">"{req.reason}"</p>}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <Btn onClick={() => approve(r)} disabled={busy[r.id]} data-testid={`refund-approve-${r.id}`}>
                    <Check className="w-3.5 h-3.5" /> {busy[r.id] ? "…" : "Approve & refund via Stripe"}
                  </Btn>
                  <Btn variant="danger" onClick={() => { setDenyModal(r); setDenyNote(""); }} disabled={busy[r.id]} data-testid={`refund-deny-${r.id}`}>
                    <XIcon className="w-3.5 h-3.5" /> Deny
                  </Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {denyModal && (
        <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setDenyModal(null)}>
          <Card className="max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166]">Deny request</div>
            <div className="font-serif text-xl mt-1">#{denyModal.id.slice(0, 8).toUpperCase()}</div>
            <p className="text-sm text-[#B9C8D5] mt-2">Add a note (optional) — the customer sees this on their booking.</p>
            <textarea value={denyNote} onChange={(e) => setDenyNote(e.target.value)} className="mt-3 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" rows={3} data-testid="refund-deny-note" />
            <div className="mt-4 flex gap-3">
              <Btn variant="ghost" onClick={() => setDenyModal(null)}>Back</Btn>
              <Btn variant="danger" onClick={submitDeny} data-testid="refund-deny-confirm">Deny request</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
